// src/components/dashboard/charts/UpcomingReviewsChart.js
import { useMemo } from 'react';
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Bar, Line } from 'recharts';
import moment from 'moment-timezone';
import ChartHeader from '../common/ChartHeader';

// Tooltip with a solid background for maximum readability
const CustomTooltip = ({ active, payload, label, isUserHourlySettingEnabled }) => {
  if (!active || !payload?.length) return null;

  const { hourData, fullDate: payloadFullDate } = payload[0].payload;
  
  // Use fullDate from payload if available, otherwise fallback to label parsing
  const fullDate = payloadFullDate 
    ? moment(payloadFullDate).format('dddd, MMM D')
    : moment().add(moment(label, 'ddd D').date() - moment().date(), 'days').format('dddd, MMM D');
  
  const isToday = payloadFullDate 
    ? payloadFullDate === moment().format('YYYY-MM-DD')
    : label === moment().format('ddd D');
    
  const currentHour = moment().hour();
  const hasHourlyData = isUserHourlySettingEnabled && hourData && Object.keys(hourData).length > 0;
  const mergedHours = {};
  
  if (hasHourlyData) {
    Object.entries(hourData).forEach(([h, c]) => {
      const hr = Number(h);
      const bucketHour = isToday && hr < currentHour ? currentHour : hr;
      mergedHours[bucketHour] = (mergedHours[bucketHour] || 0) + c;
    });
  }
  const hasVisibleHourlyData = hasHourlyData && Object.values(mergedHours).some((count) => count > 0);

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-background-secondary p-4 shadow-xl dark:bg-background-secondary z-[9999]">
      <div className="relative z-10 mb-3 border-b border-border pb-2 text-base font-semibold text-content-primary">{fullDate}</div>

      <div className="relative z-10 space-y-2">
        {payload.map((p, i) => (
          <div key={i} className="flex items-center justify-between">
            <span className="text-sm font-medium text-content-secondary">{p.name === 'Daily Reviews' ? 'Daily' : 'Total'}:</span>
            <span className="ml-3 text-sm font-semibold text-content-primary">{p.value}</span>
          </div>
        ))}
      </div>

      {hasVisibleHourlyData && (
        <>
          <div className="relative z-10 mt-3 border-t border-border pt-3">
            <div className="space-y-1 text-xs">
              {Object.entries(mergedHours)
                .filter(([, c]) => c > 0)
                .sort(([a], [b]) => a - b)
                .map(([h, c]) => (
                  <div key={h} className="flex items-center justify-between rounded-md border border-border bg-background-primary py-1.5 px-3">
                    <span className="font-medium text-content-secondary">{String(h).padStart(2, '0')}:00</span>
                    <span className="font-semibold text-primary">{c}</span>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// A dedicated empty state component
const UpcomingReviewsEmptyState = ({ title }) => (
  <div className="group relative flex h-72 w-full flex-col overflow-hidden rounded-2xl border-2 border-border bg-background-secondary p-4 backdrop-blur-sm">
    {/* Decorative background elements */}
    <div className="absolute inset-0 opacity-10">
      <div className="absolute right-0 top-0 h-24 w-24 -translate-y-12 translate-x-12 rounded-full bg-gradient-to-br from-primary to-secondary transition-transform duration-700 group-hover:scale-110" />
      <div className="absolute bottom-0 left-0 h-16 w-16 -translate-x-8 translate-y-8 rounded-full bg-gradient-to-br from-accent to-primary transition-transform duration-700 group-hover:scale-110" />
    </div>

    <div className="relative z-10">
      <ChartHeader title={title} />
    </div>

    {/* Content */}
    <div className="relative z-10 flex flex-grow flex-col items-center justify-center text-center text-content-secondary">
      <div className="relative mb-3 flex h-16 w-16 items-center justify-center rounded-full border-2 border-white/30 bg-gradient-to-br from-gray-100 to-gray-200 backdrop-blur-sm dark:from-gray-700 dark:to-gray-800">
        <div className="h-6 w-6 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-500 dark:to-gray-600" />
        <div className="absolute inset-0 rounded-full border border-dashed border-gray-400 opacity-50" />
      </div>
      <p className="text-sm font-semibold text-content-primary">No Upcoming Reviews</p>
      <p className="text-xs opacity-80">Check back later to see your forecast.</p>
    </div>
  </div>
);

const UpcomingReviewsChart = ({ data, color, yMax, gradientId, isUserHourlySettingEnabled, type = 'sentences' }) => {
  const preparedData = useMemo(
    () =>
      data.map((d, i) => ({
        ...d,
        daily: i === 0 ? d.reviews : d.reviews - (data[i - 1]?.reviews || 0),
        fullDay: moment()
          .add(moment(d.day, 'ddd D').date() - moment().date(), 'days')
          .format('ddd D'),
      })),
    [data]
  );

  // Condition to check for no data or all-zero data
  if (!data || data.length === 0 || data.every((d) => d.reviews === 0)) {
    return (
      <div className="flex w-full flex-col">
        <UpcomingReviewsEmptyState title={`${type.charAt(0).toUpperCase() + type.slice(1)} Forecast`} />
      </div>
    );
  }

  const modernColor = color || '#3B82F6';

  return (
    <div className="flex w-full flex-col">
      <div className="relative h-72 overflow-visible rounded-2xl border-2 border-border bg-background-secondary/70 backdrop-blur-sm z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-50" />
        <div className="absolute inset-0 opacity-5">
          <div className="absolute right-0 top-0 h-32 w-32 -translate-y-16 translate-x-16 rounded-full bg-primary" />
          <div className="absoluste bottom-0 left-0 h-24 w-24 translate-y-12 -translate-x-12 rounded-full bg-secondary" />
        </div>

        <div className="relative z-10 p-4">
          <ChartHeader title={`${type.charAt(0).toUpperCase() + type.slice(1)} Forecast`} />
          <div className="h-52 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={preparedData} margin={{ top: 10, right: 10, left: -20, bottom: 30 }}>
              <defs>
                <linearGradient id={`${gradientId}-glass`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={modernColor} stopOpacity={0.9} />
                  <stop offset="50%" stopColor={modernColor} stopOpacity={0.6} />
                  <stop offset="100%" stopColor={modernColor} stopOpacity={0.3} />
                </linearGradient>

                <filter id={`${gradientId}-glass-shadow`} x="-50%" y="-50%" width="200%" height="200%">
                  <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor={modernColor} floodOpacity="0.2" />
                </filter>
              </defs>

              <CartesianGrid stroke="var(--color-border)" strokeOpacity={0.2} strokeDasharray="3 3" vertical={false} />

              <XAxis
                dataKey="day"
                tick={{
                  fill: 'var(--color-content-primary)',
                  fontSize: 11,
                  fontWeight: 500,
                  angle: -45,
                  textAnchor: 'end',
                  dy: 5,
                  dx: -3,
                }}
                axisLine={false}
                tickLine={false}
                tickMargin={8}
                interval={0}
                height={50}
              />

              <YAxis
                allowDecimals={false}
                domain={[0, yMax || 'dataMax']}
                tick={{
                  fill: 'var(--color-content-secondary)',
                  fontSize: 11,
                  fontWeight: 400,
                }}
                axisLine={false}
                tickLine={false}
                tickMargin={6}
              />

              {/* UPDATED: Changed _content back to content */}
              <Tooltip
                content={<CustomTooltip isUserHourlySettingEnabled={isUserHourlySettingEnabled} />}
                cursor={{
                  fill: modernColor,
                  opacity: 0.1,
                }}
              />

              <Bar
                dataKey="daily"
                name="Daily Reviews"
                fill={`url(#${gradientId}-glass)`}
                radius={[6, 6, 0, 0]}
                stroke="none"
                filter={`url(#${gradientId}-glass-shadow)`}
              />

              <Line
                type="monotone"
                dataKey="reviews"
                name="Cumulative Total"
                stroke={modernColor}
                strokeWidth={3}
                dot={{
                  r: 4,
                  fill: modernColor,
                  strokeWidth: 2,
                  stroke: 'var(--color-background-secondary)',
                  filter: `url(#${gradientId}-glass-shadow)`,
                }}
                activeDot={{
                  r: 6,
                  fill: modernColor,
                  strokeWidth: 3,
                  stroke: 'var(--color-background-secondary)',
                  filter: `url(#${gradientId}-glass-shadow)`,
                }}
                filter={`url(#${gradientId}-glass-shadow)`}
              />
            </ComposedChart>
          </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpcomingReviewsChart;
